import { Link } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView, useMotionValue, useSpring } from "framer-motion";
import {
  BookOpen, Pencil, Languages, Brain, ArrowRight, CheckCircle2, Crown,
  Users, Award, Sparkles, Layers, Flame, Trophy, Target, ChevronRight,
  Star, Zap, MessageSquare, Quote,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CountdownBOE from "../components/CountdownBOE";
import LiveDemo from "../components/LiveDemo";
import { Button } from "../components/ui/button";

const Hero3DScene = lazy(() => import("../components/Hero3DScene"));

const LIVE_MESSAGES = [
  { who: "Carlos M.", what: "acaba de aprobar el simulacro", channel: "Simulacro" },
  { who: "María L.", what: "ha resuelto 47 preguntas seguidas", channel: "Temario" },
  { who: "Javier P.", what: "está repasando Constitución", channel: "Bloque 1" },
  { who: "Lucía R.", what: "preguntó una duda de tildes", channel: "Ortografía" },
  { who: "Andrés G.", what: "encadena 12 días de racha", channel: "Reto diario" },
  { who: "Sara V.", what: "lidera el ranking semanal", channel: "Ranking" },
  { who: "Pablo S.", what: "discute Past Simple", channel: "Inglés" },
];

function LiveCommunityChip() {
  const [idx, setIdx] = useState(0);
  const [online, setOnline] = useState(127);

  useEffect(() => {
    const t1 = setInterval(() => setIdx((i) => (i + 1) % LIVE_MESSAGES.length), 3200);
    const t2 = setInterval(() => setOnline((c) => Math.max(98, Math.min(176, c + Math.floor(Math.random() * 7) - 3))), 4500);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);
  const msg = LIVE_MESSAGES[idx];

  return (
    <Link to="/comunidad" className="group inline-flex items-center gap-3 pl-3 pr-4 py-2 rounded-full bg-white/[0.04] border border-emerald-500/20 backdrop-blur-md hover:border-emerald-500/40 transition-all shadow-[0_0_30px_-12px_rgba(16,185,129,0.5)]" data-testid="hero-live-community-chip">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
      </span>
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-300 font-mono tabular-nums whitespace-nowrap">
        <span data-testid="hero-online-count">{online}</span> en vivo
      </span>
      <span className="hidden sm:inline-block h-3.5 w-px bg-white/10" />
      <div className="hidden sm:flex items-center min-w-0 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }} className="text-xs text-slate-300 truncate max-w-[260px] md:max-w-[340px]">
            <span className="font-bold text-white">{msg.who}</span>{" "}
            <span className="text-slate-400">{msg.what}</span>{" "}
            <span className="text-gcgold font-mono text-[10px] uppercase tracking-wider ml-1">· {msg.channel}</span>
          </motion.div>
        </AnimatePresence>
      </div>
      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400 group-hover:text-emerald-300 transition-colors whitespace-nowrap">Unirse →</span>
    </Link>
  );
}

/* Magnetic button — follows cursor when hovered */
function MagneticButton({ children, className = "", to, "data-testid": testId }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 14 });
  const sy = useSpring(y, { stiffness: 180, damping: 14 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.3);
    y.set((e.clientY - r.top - r.height / 2) * 0.4);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className="inline-block">
      <Link to={to} className={className} data-testid={testId}>{children}</Link>
    </motion.div>
  );
}

/* Counter that animates when in view */
function Counter({ to, suffix = "", duration = 1.5 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{val.toLocaleString("es-ES")}{suffix}</span>;
}

/* Spotlight card that follows mouse */
function SpotlightCard({ children, className = "", ...props }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -200, y: -200 });
  return (
    <div
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
      }}
      onMouseLeave={() => setPos({ x: -200, y: -200 })}
      className={`relative overflow-hidden ${className}`}
      style={{
        "--spot-x": `${pos.x}px`,
        "--spot-y": `${pos.y}px`,
      }}
      {...props}
    >
      <div className="pointer-events-none absolute inset-0 opacity-60 transition-opacity"
        style={{ background: "radial-gradient(280px circle at var(--spot-x) var(--spot-y), rgba(212,175,55,0.12), transparent 50%)" }} />
      {children}
    </div>
  );
}

const CATEGORIES = [
  { id: "temario", name: "Temario", desc: "27 temas oficiales en 4 bloques", icon: BookOpen, count: "5.200+", accent: "emerald", featured: true },
  { id: "ortografia", name: "Ortografía", desc: "Tildes · B/V · G/J · signos", icon: Pencil, count: "2.800+", accent: "amber" },
  { id: "ingles", name: "Inglés", desc: "Nivel A2/B1 oficial", icon: Languages, count: "2.100+", accent: "sky" },
  { id: "psicotecnicos", name: "Psicotécnicos", desc: "Numérico · verbal · abstracto", icon: Brain, count: "3.400+", accent: "rose" },
];

const STEPS = [
  { num: "01", title: "Estudia por bloques", desc: "Empieza por donde estés más flojo. Tema a tema, con feedback instantáneo y explicación de cada pregunta.", icon: BookOpen },
  { num: "02", title: "Simula el examen real", desc: "Simulacros cronometrados con corrección oficial (penalización −1/3). El examen ya no te pillará por sorpresa.", icon: Target },
  { num: "03", title: "Analiza y mejora", desc: "Estadísticas, ranking, retos diarios. Sabrás exactamente cuánto te falta para tu plaza y dónde poner el foco.", icon: Trophy },
];

const TESTIMONIALS = [
  { name: "Marta Sánchez", role: "Aprobó 2025 (puesto 47)", quote: "Pasé de 6,2 a 8,9 en simulacros en 3 meses. Las estadísticas y el reto diario son adictivos.", rating: 5 },
  { name: "Iván Torres", role: "Opositor activo · Sevilla", quote: "Lo que más me ha cambiado son las falladas: dejé de repetir errores. Esto debería ser obligatorio.", rating: 5 },
  { name: "Carmen R.", role: "Aprobó 2024", quote: "Probé otras webs, ninguna como esta. El diseño no estorba y el banco de psicotécnicos es brutal.", rating: 5 },
  { name: "Diego Morales", role: "Top 10 ranking nacional", quote: "Lo de competir con otros opositores en tiempo real me mantiene enchufado. 220 días seguidos de racha.", rating: 5 },
  { name: "Lucía P.", role: "Reservista en formación", quote: "El temario por bloques está perfecto y las explicaciones de las preguntas correctas valen oro.", rating: 5 },
  { name: "Antonio J.", role: "Opositor 2026", quote: "El soporte responde en horas. Premium se paga solo con los simulacros oficiales antiguos.", rating: 5 },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <div className="min-h-screen bg-ink text-white relative overflow-x-hidden" data-testid="landing-page">
      <SEO
        title="TestOpoGuardiaCivil — Plataforma de élite para tu plaza"
        description="Plataforma de élite para la oposición a la Guardia Civil. +13.500 preguntas de temario, ortografía, inglés y psicotécnicos. Simulacros, estadísticas, comunidad y ranking."
      />

      {/* GLOBAL BACKGROUNDS */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0" style={{ background: "radial-gradient(80vw 60vh at 50% 0%, rgba(10,92,54,0.20), transparent 60%), radial-gradient(60vw 50vh at 90% 30%, rgba(212,175,55,0.08), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.045]" style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      </div>

      <Header />

      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-[88vh] md:min-h-[92vh] flex items-center pt-32 md:pt-24 pb-16 overflow-hidden" data-testid="hero-section">
        {/* 3D Canvas */}
        <Suspense fallback={null}>
          <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
            <Hero3DScene />
          </motion.div>
        </Suspense>

        {/* Vignette to keep text readable on top of 3D */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60vw 40vh at 50% 50%, rgba(5,9,7,0.65), transparent 70%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5 backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gcgold opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gcgold" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">Convocatoria 2026 · BOE abierto</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="mb-6">
            <LiveCommunityChip />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-7xl lg:text-[8rem] xl:text-[9rem] font-extrabold tracking-[-0.04em] leading-[0.9] text-balance text-white"
            data-testid="hero-headline"
          >
            Tu plaza en la
            <br />
            <span
              className="block font-serif italic font-bold text-gcgold tracking-tight relative z-10"
              style={{
                fontFamily: "'Cormorant Garamond', 'Bricolage Grotesque', serif",
                fontSize: "1.15em",
                textShadow:
                  "0 0 30px rgba(212,175,55,0.55), 0 0 60px rgba(212,175,55,0.35), 0 2px 0 rgba(0,0,0,0.6)",
                WebkitTextStroke: "0.5px rgba(212,175,55,0.4)",
              }}
            >
              Guardia Civil.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-7 mx-auto max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed"
          >
            +13.500 preguntas oficiales, simulacros cronometrados, ranking en vivo y la única IA que <em className="text-gcgold not-italic font-semibold">conoce tu oposición</em>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <MagneticButton
              to="/premium"
              data-testid="cta-register"
              className="group inline-flex items-center gap-2 h-14 px-8 rounded-2xl bg-gcgold hover:bg-gcgold-light text-ink font-extrabold text-base shadow-[0_10px_50px_-10px_rgba(212,175,55,0.55)] transition-all"
            >
              Empieza gratis ahora
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </MagneticButton>
            <Link to="/login" className="text-sm font-medium text-white/70 hover:text-white underline decoration-gcgold/40 decoration-2 underline-offset-8 hover:decoration-gcgold transition-colors" data-testid="cta-login">
              Ya tengo cuenta →
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-14 flex items-center justify-center gap-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40"
          >
            <span>· Sin tarjeta ·</span>
            <span>· 7 días Premium gratis ·</span>
            <span className="hidden sm:inline">· Cancelas cuando quieras ·</span>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/40"
        >
          <span>Scroll</span>
          <div className="h-10 w-px bg-gradient-to-b from-gcgold/60 to-transparent animate-pulse" />
        </motion.div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <section className="relative border-y border-white/5 bg-ink-900/60 backdrop-blur-sm" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: 13500, suffix: "+", label: "Preguntas", icon: Layers },
            { value: 27, suffix: "", label: "Temas oficiales", icon: BookOpen },
            { value: 24000, suffix: "+", label: "Opositores activos", icon: Users },
            { value: 98, suffix: "%", label: "Satisfacción", icon: Award },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              viewport={{ once: true, margin: "-50px" }}
              className="text-center sm:text-left"
            >
              <s.icon className="h-4 w-4 text-gcgold mx-auto sm:mx-0 mb-3 opacity-70" />
              <div className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter tabular-nums">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== LIVE DEMO ===== */}
      <LiveDemo />

      {/* ===== BENTO CATEGORIES ===== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32" data-testid="categories-section">
        <SectionLabel>Plan de estudio</SectionLabel>
        <SectionTitle>
          Cuatro frentes.<br />
          <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Una sola misión.</span>
        </SectionTitle>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            const featured = c.featured;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24, rotateX: 8 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: "-80px" }}
                className={featured ? "md:col-span-4 md:row-span-2" : "md:col-span-2"}
              >
                <SpotlightCard className={`group relative h-full rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.03] to-transparent backdrop-blur-md p-7 hover:border-gcgold/30 transition-all ${featured ? "min-h-[420px]" : "min-h-[200px]"}`}>
                  <div className="flex flex-col h-full justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-5">
                        <div className="h-11 w-11 rounded-xl bg-gcgreen/15 border border-gcgreen/20 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-gcgreen-light" />
                        </div>
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-gcgold">{c.count}</span>
                      </div>
                      <h3 className={`font-display font-extrabold text-white tracking-tighter ${featured ? "text-4xl sm:text-5xl" : "text-2xl"}`}>
                        {c.name}
                      </h3>
                      <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.desc}</p>
                    </div>
                    {featured && (
                      <div className="mt-8 grid grid-cols-2 gap-3">
                        {["Constitución", "Derecho Penal", "Procedimiento", "UE"].map((t) => (
                          <div key={t} className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05] text-xs text-white/60 font-mono">
                            {t}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-6 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-gcgold opacity-0 group-hover:opacity-100 transition-opacity">
                      Entrar <ChevronRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </SpotlightCard>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-32" data-testid="how-it-works-section">
        <SectionLabel>Cómo funciona</SectionLabel>
        <SectionTitle>
          Tres pasos hasta tu <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>plaza</span>.
        </SectionTitle>

        <div className="mt-20 relative">
          {/* Vertical line */}
          <div className="absolute left-7 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gcgold/30 to-transparent" aria-hidden />

          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const left = i % 2 === 0;
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: "-100px" }}
                className={`relative flex gap-6 mb-16 last:mb-0 ${left ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                <div className="flex-1 md:max-w-md">
                  <div className={`bg-ink-800/60 backdrop-blur-md border border-white/5 rounded-2xl p-7 hover:border-gcgold/30 transition-colors ${left ? "md:mr-12" : "md:ml-12"}`}>
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-gcgold/70">Paso {step.num}</div>
                    <h3 className="mt-3 font-display text-2xl font-extrabold text-white tracking-tight">{step.title}</h3>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
                <div className="relative z-10 flex-shrink-0">
                  <div className="h-14 w-14 rounded-2xl bg-gcgold border-2 border-gcgold/40 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.4)]">
                    <Icon className="h-6 w-6 text-ink" />
                  </div>
                </div>
                <div className="hidden md:block flex-1" />
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== TESTIMONIALS DUAL MARQUEE ===== */}
      <section className="relative py-24 overflow-hidden border-y border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 text-center mb-12">
          <SectionLabel>Opositores reales</SectionLabel>
          <SectionTitle>Lo que dicen <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>quienes ya están dentro</span>.</SectionTitle>
        </div>

        <div className="relative">
          {/* Edge masks */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-ink to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-ink to-transparent z-10 pointer-events-none" />

          <MarqueeRow items={TESTIMONIALS.slice(0, 3)} direction="left" speed={50} />
          <div className="h-5" />
          <MarqueeRow items={TESTIMONIALS.slice(3)} direction="right" speed={45} />
        </div>
      </section>

      {/* ===== COUNTDOWN + PREMIUM CTA ===== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32" data-testid="premium-cta-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Countdown card */}
          <SpotlightCard className="bg-ink-800/60 border border-white/5 rounded-3xl p-8 flex flex-col justify-between min-h-[400px]">
            <div>
              <SectionLabel>BOE en vivo</SectionLabel>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-white">
                El examen no espera.<br />
                <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tu plaza tampoco.</span>
              </h2>
            </div>
            <div className="mt-6">
              <CountdownBOE />
            </div>
          </SpotlightCard>

          {/* Premium card */}
          <SpotlightCard className="relative bg-gradient-to-br from-gcgold/15 via-gcgreen/5 to-ink-800 border border-gcgold/30 rounded-3xl p-8 lg:p-10 overflow-hidden">
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-gcgold/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gcgold/15 border border-gcgold/30 text-gcgold font-mono text-[11px] uppercase tracking-[0.22em] mb-5">
                <Crown className="h-3 w-3" /> Premium
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-white">
                La ventaja <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>desleal</span>.
              </h2>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Simulacros oficiales históricos",
                  "Estadísticas predictivas avanzadas",
                  "Tests ilimitados sin esperas",
                  "Soporte prioritario < 4h",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-white/85">
                    <CheckCircle2 className="h-4 w-4 text-gcgold flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold text-white tracking-tighter tabular-nums">5,50€</span>
                <span className="text-sm text-white/50 font-mono">/mes · plan anual</span>
              </div>
              <p className="mt-2 text-xs text-white/50">Empieza con <strong className="text-white">7 días gratis</strong>, sin tarjeta.</p>
              <MagneticButton
                to="/premium"
                data-testid="cta-premium"
                className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-2xl bg-gcgold hover:bg-gcgold-light text-ink font-extrabold shadow-[0_10px_40px_-10px_rgba(212,175,55,0.55)]"
              >
                Desbloquear Premium <ArrowRight className="h-4 w-4" />
              </MagneticButton>
            </div>
          </SpotlightCard>
        </div>
      </section>

      {/* ===== HERO QUOTE ===== */}
      <section className="relative py-32 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-7xl mx-auto px-4 text-center"
        >
          <div className="font-display font-black text-white/[0.04] tracking-tighter leading-none select-none text-[14vw] sm:text-[12vw]" aria-hidden>
            TU PLAZA TE ESPERA
          </div>
          <div className="-mt-[7vw] sm:-mt-[6vw] relative z-10">
            <p className="font-display text-2xl sm:text-3xl text-white/80 max-w-2xl mx-auto">
              No es suerte. Es <span className="font-serif italic text-gcgold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>preparación deliberada</span>.
            </p>
            <MagneticButton
              to="/premium"
              data-testid="cta-final"
              className="mt-10 inline-flex items-center gap-2 h-14 px-10 rounded-2xl bg-white text-ink hover:bg-gcgold font-extrabold text-base transition-colors shadow-[0_15px_60px_-15px_rgba(255,255,255,0.5)]"
            >
              Crear cuenta gratis <ArrowRight className="h-5 w-5" />
            </MagneticButton>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}

/* ---------- Internal helpers ---------- */
function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="h-px w-8 bg-gcgold/50" />
      <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-gcgold">{children}</span>
    </div>
  );
}
function SectionTitle({ children }) {
  return (
    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] text-white leading-[0.95]">
      {children}
    </h2>
  );
}

function MarqueeRow({ items, direction = "left", speed = 50 }) {
  const dup = [...items, ...items, ...items];
  return (
    <div className="overflow-hidden">
      <motion.div
        className="flex gap-5 w-max"
        animate={{ x: direction === "left" ? ["0%", "-33.333%"] : ["-33.333%", "0%"] }}
        transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
      >
        {dup.map((t, i) => (
          <div key={i} className="w-[340px] sm:w-[400px] flex-shrink-0 bg-ink-800/60 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:border-gcgold/30 transition-colors">
            <Quote className="h-5 w-5 text-gcgold/60 mb-3" />
            <p className="text-sm text-white/80 leading-relaxed mb-4 min-h-[80px]">&ldquo;{t.quote}&rdquo;</p>
            <div className="flex items-center gap-3 pt-3 border-t border-white/5">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gcgreen/40 to-gcgold/30 flex items-center justify-center font-bold text-white">
                {t.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate">{t.name}</div>
                <div className="text-[10px] uppercase font-mono tracking-wider text-white/45">{t.role}</div>
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="h-3 w-3 fill-gcgold text-gcgold" />
                ))}
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
